import { API } from "@/api"
import { ClientConfigData, HttpAPIError } from "@/api/model"
import { KCSDK } from "@/sdk"
import { store } from "@/store"
import { selectFpHash } from "@/stores/auth_slice"
import { ClientConfigCache } from "@/utils/storage"
// [日志上传功能已注释] import { collectAndZipLogs } from "@/utils/log_collector"
import { UserUsecase } from "./user"

/**
 * 客户端动态配置周期性拉取（对齐 android ClientConfigUseCase）
 *
 * 登录态下每 [_interval] 分钟请求一次 GET /api/client-config，将配置（ETag + body）
 * 缓存到 ClientConfigCache。退出登录时停止。首次请求不带 If-None-Match；后续
 * 携带本地 ETag 发起条件请求，304 时复用本地缓存。拉取到新配置后透传给远端 agent，
 * 并在 enableLogUpload===1 时触发日志上传（按 requestId 去重）。
 */

const TAG = "ClientConfigUseCase"
const FETCH_INTERVAL_MS = 5 * 60 * 1000

let hasInit = false
let isRunning = false
let timer: ReturnType<typeof setInterval> | null = null

/** 初始化，使用前需在应用启动时调用 */
function init(): void {
  if (hasInit) {
    return
  }
  hasInit = true
}

/** 开启周期性拉取（登录成功 / 启动时已登录时调用），幂等 */
function start(): void {
  if (isRunning) {
    return
  }
  isRunning = true
  console.log(`[${TAG}] start periodic fetch`)

  void fetch()

  if (timer !== null) {
    clearInterval(timer)
  }
  timer = setInterval(() => {
    // 无网络跳过
    if (!navigator.onLine) {
      return
    }
    void fetch()
  }, FETCH_INTERVAL_MS)
}

/** 停止周期性拉取（退出登录 / token 失效时调用），幂等 */
function stop(): void {
  if (!isRunning) {
    return
  }
  isRunning = false
  console.log(`[${TAG}] stop periodic fetch`)

  if (timer !== null) {
    clearInterval(timer)
    timer = null
  }
}

/** 清空缓存并立即拉取一次配置
 *
 * 用于用户切换 / 重新登录场景：旧用户的 ETag、body、已上传 requestId 不应沿用，
 * 必须清空后按新用户身份重新拉取，否则可能命中 304 复用错误配置，或被旧
 * requestId 去重跳过。若周期拉取未启动，会先启动再拉取。
 */
function reload(): void {
  ClientConfigCache.clear()
  if (!isRunning) {
    start()
  } else {
    void fetch()
  }
}

/** 执行一次拉取
 *
 * 持有全部业务逻辑：读取本地 ETag 作为 If-None-Match 发起条件请求；
 * 304 时复用本地缓存；200 时校验业务码并将配置与 ETag 写入本地缓存。
 */
async function fetch(): Promise<void> {
  if (!isRunning) {
    return
  }
  // 未登录跳过
  const user = UserUsecase.getCurrentUser()
  if (user === null) {
    return
  }

  const etag = ClientConfigCache.getEtag()
  const state = store.getState()
  const fpHash = selectFpHash(state)
  const queryParameters: Record<string, string | number> = {
    platform: "web",
    sn: fpHash || "unknown",
    userName: user.account,
  }

  const res = await API.getClientConfig({
    queryParameters,
    ifNoneMatch: etag ?? undefined,
  })
  if (res instanceof HttpAPIError) {
    console.warn(`[${TAG}] fetch failed:`, res)
    return
  }

  // 304 Not Modified：配置未变更，复用本地缓存
  if (res.statusCode === 304) {
    return
  }

  // 业务错误
  if (res.code !== 0) {
    console.warn(`[${TAG}] fetch biz error: ${res.code} ${res.msg}`)
    return
  }

  const data = res.data
  if (data == null) {
    return
  }

  if (res.etag) {
    ClientConfigCache.setEtag(res.etag)
  }

  deal(data)
}

/** 处理客户端配置
 *
 * 持久化配置 → 透传给远端 agent → 根据 enableLogUpload 决定是否上传日志。
 */
function deal(data: ClientConfigData): void {
  ClientConfigCache.setBody(data)

  // 透传给远端 agent（WebRTC 未连接时 KCSDK 内部静默跳过）
  try {
    KCSDK.sendClientConfig(data as unknown as Record<string, unknown>)
  } catch (e) {
    console.error(`[${TAG}] sendClientConfig error:`, e)
  }

  // [日志上传功能已注释]
  // if (data.enableLogUpload === 1) {
  //   void uploadLog(data)
  // }
}

/* [日志上传功能已注释]
 * 上传日志
 *
 * 同一 logUploadRequestId 只上传一次（成功后记录到 ClientConfigCache）。
async function uploadLog(data: ClientConfigData): Promise<void> {
  const reqId = data.logUploadRequestId
  const url = data.logUploadUrl
  if (!reqId || !url) {
    return
  }

  // 同一 requestId 已上传过，跳过
  if (ClientConfigCache.getUploadedRequestId() === reqId) {
    return
  }

  const user = UserUsecase.getCurrentUser()
  const userName = user?.account ?? "unknown"
  const sn = selectFpHash(store.getState()) || "unknown"

  // 收集内存日志并打包为 zip
  const zipBody = await collectAndZipLogs({ userName, sn, reqId })
  if (zipBody === null) {
    // 无日志可上传
    return
  }

  try {
    const res = await API.uploadLogZip(url, zipBody)
    if (res instanceof HttpAPIError) {
      console.warn(`[${TAG}] upload log failed:`, res)
      return
    }
    ClientConfigCache.setUploadedRequestId(reqId)
  } catch (e) {
    console.warn(`[${TAG}] upload log error:`, e)
  }
}
*/

export const ClientConfigUseCase = {
  init,
  start,
  stop,
  reload,
}
